/**
 * StripHistoryModal.jsx  v2
 * Usa RangoFechas — default mes actual
 */

import { useState, useEffect } from 'react';
import {
    Box, CircularProgress, Dialog, DialogContent, DialogTitle,
    IconButton, Paper, Stack, Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import RangoFechas, { mesActual } from './Rangofechas';
import { TANKS } from './Hipergas.utils';

const TITULOS = {
    nivel: 'Nivel promedio — histórico',
    propano: 'Propano (tn) — histórico',
    butano: 'Butano (tn) — histórico',
    total: 'Total disponible (tn) — histórico',
};

export default function StripHistoryModal({ open, tipo, configs, apiUrl, onClose }) {
    const [rango, setRango] = useState(() => mesActual());
    const [historico, setHistorico] = useState([]);
    const [loading, setLoading] = useState(false);

    const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };

    useEffect(() => {
        if (open) setRango(mesActual());
    }, [open]);

    useEffect(() => {
        if (!open || !tipo) return;
        setLoading(true);
        const dias = (new Date(rango.to) - new Date(rango.from)) / (1000 * 60 * 60 * 24);
        const intervalo = dias <= 1 ? 10 : dias <= 7 ? 60 : dias <= 30 ? 120 : 240;

        fetch(`${apiUrl}/api/alvear/historico-masa?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}&intervalo=${intervalo}&limit=500`, { headers })
            .then((r) => r.json())
            .then((d) => setHistorico(d))
            .catch(() => setHistorico([]))
            .finally(() => setLoading(false));
    }, [open, tipo, rango, apiUrl]);

    if (!tipo) return null;

    // Construir series y dataset según tipo
    const buildChart = () => {
        if (tipo === 'nivel') {
            return {
                series: TANKS.map((t) => ({
                    dataKey: `TK_${t.id}_pct`, label: t.label,
                    color: t.color, showMark: false, curve: 'monotoneX',
                })),
                dataset: historico,
                yLabel: '%',
            };
        }

        if (tipo === 'propano') {
            const tanques = TANKS.filter((t) => (configs[t.id]?.producto || 'propano') === 'propano');
            return {
                series: tanques.map((t) => ({
                    dataKey: `TK_${t.id}_tn`, label: t.label,
                    color: t.color, showMark: false, curve: 'monotoneX',
                })),
                dataset: historico,
                yLabel: 'tn',
            };
        }

        if (tipo === 'butano') {
            const tanques = TANKS.filter((t) => configs[t.id]?.producto === 'butano');
            return {
                series: tanques.map((t) => ({
                    dataKey: `TK_${t.id}_tn`, label: t.label,
                    color: t.color, showMark: false, curve: 'monotoneX',
                })),
                dataset: historico,
                yLabel: 'tn',
            };
        }

        if (tipo === 'total') {
            // Agregar columnas de suma por producto al dataset
            const dataset = historico.map((p) => {
                const tnPropano = TANKS
                    .filter((t) => (configs[t.id]?.producto || 'propano') === 'propano')
                    .reduce((a, t) => a + (p[`TK_${t.id}_tn`] || 0), 0);
                const tnButano = TANKS
                    .filter((t) => configs[t.id]?.producto === 'butano')
                    .reduce((a, t) => a + (p[`TK_${t.id}_tn`] || 0), 0);
                return { ...p, propano_total: parseFloat(tnPropano.toFixed(2)), butano_total: parseFloat(tnButano.toFixed(2)) };
            });
            return {
                series: [
                    { dataKey: 'propano_total', label: 'Propano', color: '#38BDF8', showMark: false, curve: 'monotoneX' },
                    { dataKey: 'butano_total', label: 'Butano', color: '#FB923C', showMark: false, curve: 'monotoneX' },
                    { dataKey: 'total_tn', label: 'Total', color: '#22C55E', showMark: false, curve: 'monotoneX' },
                ],
                dataset,
                yLabel: 'tn',
            };
        }

        return { series: [], dataset: [], yLabel: '' };
    };

    const { series, dataset, yLabel } = buildChart();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}>

            <DialogTitle sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {TITULOS[tipo]}
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {/* Selector de rango */}
                <Box sx={{ mt: 1.5 }}>
                    <RangoFechas value={rango} onChange={setRango} />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {loading ? (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress size={28} />
                        <Typography variant="caption" color="text.disabled" mt={1}>Cargando histórico...</Typography>
                    </Stack>
                ) : dataset.length === 0 ? (
                    <Stack alignItems="center" py={4}>
                        <Typography variant="body2" color="text.disabled">Sin datos en el período seleccionado</Typography>
                    </Stack>
                ) : (
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                        {/* Leyenda */}
                        <Stack direction="row" spacing={2} mb={1} flexWrap="wrap">
                            {series.map((s) => (
                                <Stack key={s.dataKey} direction="row" alignItems="center" spacing={0.5}>
                                    <Box sx={{ width: 12, height: 2, bgcolor: s.color, borderRadius: 1 }} />
                                    <Typography variant="caption" color="text.disabled" fontSize={10}>{s.label}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                        <LineChart
                            dataset={dataset}
                            series={series}
                            xAxis={[{
                                dataKey: 'fecha', scaleType: 'band',
                                tickLabelStyle: { fontSize: 8 },
                                tickInterval: (_, i) => i % Math.ceil(dataset.length / 8) === 0,
                            }]}
                            yAxis={[{ tickLabelStyle: { fontSize: 9 }, label: yLabel }]}
                            height={320}
                            margin={{ left: 50, right: 16, top: 8, bottom: 32 }}
                            slotProps={{ legend: { hidden: true } }}
                        />
                    </Paper>
                )}
            </DialogContent>
        </Dialog>
    );
}